import React, { useState, useEffect, useRef } from 'react'
import { UserLayout } from 'components/layout/UserLayout'
import { useGetBusinessID, useGetBusiness, NestAPI } from '@marketing-milk/frontend'
import { AdCreative, AIGenerationMode, CopySuggestion } from '@marketing-milk/interfaces'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import { AdSelectionGrid } from './components/AdSelectionGrid'
import { ConfigureStep } from './components/ConfigureStep'
import { VariantsStep } from './components/VariantsStep'
import { ResultStep } from './components/ResultStep'
import { ImportModal } from './components/ImportModal'
import { WizardHeader } from './components/WizardHeader'
import { CopyGenerationPanel } from './components/CopyGenerationPanel'
import { SaveAsImageModal } from './components/SaveAsImageModal'
import { BusinessFolderModal } from './components/BusinessFolderModal'
import { useAIStudio, useBusinessFolderImages } from './useAIStudio'
import { useGooglePicker, PickerFile } from './useGooglePicker'

type Step = 'select' | 'configure' | 'variants' | 'result'

type Variant = { jobID: string | null; resultImageUrl: string | null; isGenerating: boolean }

type SelectedPickerFile = PickerFile & { dataUrl: string; accessToken: string }

const EMPTY_VARIANTS: [Variant, Variant, Variant] = [
  { jobID: null, resultImageUrl: null, isGenerating: false },
  { jobID: null, resultImageUrl: null, isGenerating: false },
  { jobID: null, resultImageUrl: null, isGenerating: false },
]

export function AIStudio() {
  const businessID = useGetBusinessID()
  const business = useGetBusiness()
  const history = useHistory()

  const [step, setStep] = useState<Step>('select')
  const [selectedCreatives, setSelectedCreatives] = useState<AdCreative[]>([])
  const [pickerFiles, setPickerFiles] = useState<SelectedPickerFile[]>([])
  const [mode, setMode] = useState<AIGenerationMode>('both')
  const [textSubMode, setTextSubMode] = useState<
    'describe' | 'brand-voice' | 'describe-suggestions'
  >('describe')
  const [prompt, setPrompt] = useState('')
  const [imagePrompt, setImagePrompt] = useState('')
  const [textPrompt, setTextPrompt] = useState('')
  const [variants, setVariants] = useState<[Variant, Variant, Variant]>(EMPTY_VARIANTS)
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSaveAsImageModal, setShowSaveAsImageModal] = useState(false)
  const [showBusinessFolderModal, setShowBusinessFolderModal] = useState(false)
  const [selectedCopy, setSelectedCopy] = useState<CopySuggestion | undefined>(undefined)

  useEffect(() => {
    setStep('select')
    setSelectedCreatives([])
    setPickerFiles([])
    setMode('both')
    setTextSubMode('describe')
    setPrompt('')
    setImagePrompt('')
    setTextPrompt('')
    setVariants(EMPTY_VARIANTS)
    setSelectedVariantIndex(null)
    setIsSubmitting(false)
    setShowImportModal(false)
    setShowSaveAsImageModal(false)
    setShowBusinessFolderModal(false)
    setSelectedCopy(undefined)
  }, [businessID])

  const { generateAd, importAdImage, importCreative } = useAIStudio(businessID)
  const { openPicker } = useGooglePicker()
  const { data: businessFolderImages = [], isLoading: isLoadingFolderImages } =
    useBusinessFolderImages(businessID)
  const lastDriveDataUrlsRef = useRef<string[]>([])

  const MAX_SELECTIONS = 13
  const totalSelected = selectedCreatives.length + pickerFiles.length

  const toggleCreative = (creative: AdCreative) => {
    setSelectedCreatives(prev => {
      if (prev.some(c => c.id === creative.id)) return prev.filter(c => c.id !== creative.id)
      if (totalSelected >= MAX_SELECTIONS) return prev
      return [...prev, creative]
    })
  }

  const handlePickerSelect = (files: PickerFile[], accessToken: string) => {
    setPickerFiles(prev => {
      const existingIDs = new Set(prev.map(f => f.id))
      const slots = MAX_SELECTIONS - totalSelected
      const toAdd = files
        .filter(f => !existingIDs.has(f.id))
        .slice(0, slots)
        .map(f => ({ ...f, dataUrl: '', accessToken }))
      return [...prev, ...toAdd]
    })
  }

  const removePickerFile = (id: string) => {
    setPickerFiles(prev => prev.filter(f => f.id !== id))
  }

  const handleBusinessFolderSelect = async (files: PickerFile[]) => {
    const existingIDs = new Set(pickerFiles.map(f => f.id))
    const slots = MAX_SELECTIONS - totalSelected
    const toAdd = files.filter(f => !existingIDs.has(f.id)).slice(0, slots)
    if (toAdd.length === 0) return

    // Resolve data URLs via backend (service account)
    const resolved = await Promise.allSettled(
      toAdd.map(async f => {
        const response = await NestAPI.get<{ dataUrl: string }>(
          `businesses/${businessID}/ai-studio/business-folder-images/${f.id}/data`
        )
        return { ...f, dataUrl: response.data.dataUrl, accessToken: '' }
      })
    )

    const successful = resolved
      .filter((r): r is PromiseFulfilledResult<SelectedPickerFile> => r.status === 'fulfilled')
      .map(r => r.value)

    if (successful.length < toAdd.length) {
      toast.warn('Some images could not be loaded from the business folder.')
    }

    setPickerFiles(prev => [...prev, ...successful])
  }

  // Downloads any pending picker images and returns the full set of data URLs
  const resolveDriveDataUrls = async (): Promise<string[] | null> => {
    const pickerDownloaded = pickerFiles.filter(f => f.dataUrl).map(f => f.dataUrl)
    const pickerPending = pickerFiles.filter(f => !f.dataUrl)

    if (pickerPending.length === 0) return pickerDownloaded

    try {
      const results = await Promise.all(
        pickerPending.map(async f => {
          const res = await fetch(`https://www.googleapis.com/drive/v3/files/${f.id}?alt=media`, {
            headers: { Authorization: `Bearer ${f.accessToken}` },
          })
          const blob = await res.blob()
          const dataUrl = await new Promise<string>(resolve => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(blob)
          })
          return { id: f.id, dataUrl }
        })
      )
      setPickerFiles(prev =>
        prev.map(f => {
          const downloaded = results.find(r => r.id === f.id)
          return downloaded ? { ...f, dataUrl: downloaded.dataUrl } : f
        })
      )
      return [...pickerDownloaded, ...results.map(r => r.dataUrl)]
    } catch {
      toast.error('Failed to download picker images. Please try again.')
      return null
    }
  }

  const handleGenerate = async () => {
    if (mode === 'both') {
      if (!imagePrompt.trim() || !textPrompt.trim()) {
        toast.warn('Enter prompts for both the image and the copy.')
        return
      }
    } else if (mode === 'image') {
      if (!imagePrompt.trim()) {
        toast.warn('Enter a prompt describing what you want to generate.')
        return
      }
    } else {
      if (textSubMode !== 'brand-voice' && !prompt.trim()) {
        toast.warn('Enter a prompt describing what you want to generate.')
        return
      }
    }

    setIsSubmitting(true)
    const driveDataUrls = await resolveDriveDataUrls()
    if (driveDataUrls === null) {
      setIsSubmitting(false)
      return
    }
    lastDriveDataUrlsRef.current = driveDataUrls

    const textEffectivePrompt =
      textSubMode === 'brand-voice'
        ? "Rewrite this ad's copy to authentically reflect our brand's voice and personality, based on our existing ad history. Keep the same general message but rewrite in our established style."
        : textSubMode === 'describe-suggestions'
        ? `${prompt.trim()} Also incorporate our brand voice and style from existing ads.`
        : prompt

    const effectivePrompt =
      mode === 'both'
        ? `Image: ${imagePrompt}\n\nCopy: ${textPrompt}`
        : mode === 'image'
        ? imagePrompt
        : textEffectivePrompt

    const payload = {
      creativeIDs: selectedCreatives.map(c => c.id),
      driveImageDataUrls: driveDataUrls,
      mode,
      prompt: effectivePrompt,
    }

    setVariants([
      { jobID: null, resultImageUrl: null, isGenerating: true },
      { jobID: null, resultImageUrl: null, isGenerating: true },
      { jobID: null, resultImageUrl: null, isGenerating: true },
    ])
    setSelectedVariantIndex(null)
    setIsSubmitting(false)
    setStep('variants')

    const results = await Promise.allSettled([
      generateAd(payload),
      generateAd(payload),
      generateAd(payload),
    ])

    setVariants(
      results.map(r => ({
        jobID: r.status === 'fulfilled' ? r.value.jobID : null,
        resultImageUrl: r.status === 'fulfilled' ? r.value.resultImageUrl : null,
        isGenerating: false,
      })) as [Variant, Variant, Variant]
    )
  }

  const handleReprompt = async (index: number, newPrompt: string) => {
    const currentResultUrl = variants[index].resultImageUrl
    if (!currentResultUrl) return

    if (selectedVariantIndex === index) setSelectedVariantIndex(null)

    setVariants(prev => {
      const updated = [...prev] as [Variant, Variant, Variant]
      updated[index] = { jobID: null, resultImageUrl: null, isGenerating: true }
      return updated
    })

    try {
      const result = await generateAd({
        creativeIDs: selectedCreatives.map(c => c.id),
        // Include current result image as additional visual reference
        driveImageDataUrls: [currentResultUrl],
        mode,
        prompt: newPrompt,
      })
      setVariants(prev => {
        const updated = [...prev] as [Variant, Variant, Variant]
        updated[index] = {
          jobID: result.jobID,
          resultImageUrl: result.resultImageUrl,
          isGenerating: false,
        }
        return updated
      })
    } catch {
      toast.error('Failed to regenerate variant. Please try again.')
      setVariants(prev => {
        const updated = [...prev] as [Variant, Variant, Variant]
        updated[index] = { jobID: null, resultImageUrl: null, isGenerating: false }
        return updated
      })
    }
  }

  const handleRetry = async (index: number) => {
    const effectivePrompt =
      mode === 'both'
        ? `Image: ${imagePrompt}\n\nCopy: ${textPrompt}`
        : mode === 'image'
        ? imagePrompt
        : prompt

    setVariants(prev => {
      const updated = [...prev] as [Variant, Variant, Variant]
      updated[index] = { jobID: null, resultImageUrl: null, isGenerating: true }
      return updated
    })

    try {
      const result = await generateAd({
        creativeIDs: selectedCreatives.map(c => c.id),
        driveImageDataUrls: lastDriveDataUrlsRef.current,
        mode,
        prompt: effectivePrompt,
      })
      setVariants(prev => {
        const updated = [...prev] as [Variant, Variant, Variant]
        updated[index] = {
          jobID: result.jobID,
          resultImageUrl: result.resultImageUrl,
          isGenerating: false,
        }
        return updated
      })
    } catch {
      toast.error('Failed to retry variant. Please try again.')
      setVariants(prev => {
        const updated = [...prev] as [Variant, Variant, Variant]
        updated[index] = { jobID: null, resultImageUrl: null, isGenerating: false }
        return updated
      })
    }
  }

  const handleImportAsImage = async (name: string) => {
    const jobID = selectedVariantIndex !== null ? variants[selectedVariantIndex].jobID : null
    if (!jobID) {
      toast.error('No variant selected.')
      return
    }
    try {
      await importAdImage({ jobID, creativeName: name })
    } catch {
      toast.error('Failed to create ad image. Please try again.')
      throw new Error('import failed')
    }
  }

  const handleImportAsCreative = async (name: string, copy?: CopySuggestion) => {
    const jobID = selectedVariantIndex !== null ? variants[selectedVariantIndex].jobID : null
    if (!jobID) {
      toast.error('No variant selected.')
      return
    }
    try {
      const creative = await importCreative({
        jobID,
        creativeName: name,
        headline: copy?.headline,
        description: copy?.description,
        graphicText: copy?.graphicText,
        primaryText: copy?.primaryText,
        callToAction: copy?.callToAction,
      })
      history.push(`/${businessID}/creatives/${creative.id}`)
    } catch {
      toast.error('Failed to create creative. Please try again.')
      throw new Error('import failed')
    }
  }

  const selectedJob = selectedVariantIndex !== null ? variants[selectedVariantIndex] : undefined

  const stepIndex = step === 'select' ? 0 : step === 'configure' ? 1 : step === 'variants' ? 2 : 3

  return (
    <UserLayout>
      <div className="flex flex-col w-full h-full max-h-full">
        <WizardHeader currentStep={stepIndex} />

        {step === 'select' && (
          <AdSelectionGrid
            businessID={businessID}
            selectedCreatives={selectedCreatives}
            onToggleSelect={toggleCreative}
            totalSelected={selectedCreatives.length}
            onNext={() => setStep('configure')}
          />
        )}

        {step === 'configure' && (
          <ConfigureStep
            businessID={businessID}
            selectedCreatives={selectedCreatives}
            pickerFiles={pickerFiles}
            onOpenPicker={() => openPicker(handlePickerSelect)}
            onOpenBusinessFolder={() => setShowBusinessFolderModal(true)}
            onRemovePickerFile={removePickerFile}
            hasBusinessFolder={!!business.googleDriveFolderID}
            totalSelected={totalSelected}
            mode={mode}
            textSubMode={textSubMode}
            onTextSubModeChange={setTextSubMode}
            prompt={prompt}
            onPromptChange={setPrompt}
            imagePrompt={imagePrompt}
            textPrompt={textPrompt}
            onImagePromptChange={setImagePrompt}
            onTextPromptChange={setTextPrompt}
            onModeChange={setMode}
            onBack={() => setStep('select')}
            onGenerate={handleGenerate}
            isSubmitting={isSubmitting}
          />
        )}

        {step === 'variants' && (
          <VariantsStep
            variants={variants}
            selectedIndex={selectedVariantIndex}
            onSelect={setSelectedVariantIndex}
            onReprompt={handleReprompt}
            onRetry={handleRetry}
            onRetryAll={() => Promise.all([0, 1, 2].map(i => handleRetry(i)))}
            onBack={() => setStep('configure')}
            onNext={() => setStep('result')}
          />
        )}

        {step === 'result' && selectedJob?.resultImageUrl && selectedVariantIndex !== null && (
          <ResultStep
            jobID={variants[selectedVariantIndex].jobID!}
            resultImageUrl={selectedJob.resultImageUrl}
            onBack={() => setStep('variants')}
            onStartOver={() => {
              setStep('select')
              setPrompt('')
              setImagePrompt('')
              setTextPrompt('')
              setTextSubMode('describe')
              setSelectedCreatives([])
              setPickerFiles([])
              setVariants(EMPTY_VARIANTS)
              setSelectedVariantIndex(null)
              setSelectedCopy(undefined)
            }}
            onSaveAsImage={() => setShowSaveAsImageModal(true)}
            onImport={() => setShowImportModal(true)}
            copyPanel={
              <CopyGenerationPanel
                businessID={businessID}
                jobID={variants[selectedVariantIndex].jobID}
                onSelectSuggestion={suggestion => {
                  setSelectedCopy(suggestion)
                  setShowImportModal(true)
                }}
              />
            }
          />
        )}
      </div>

      {showImportModal && (
        <ImportModal
          onImportAsCreative={handleImportAsCreative}
          onClose={() => {
            setShowImportModal(false)
            setSelectedCopy(undefined)
          }}
          initialCopy={selectedCopy}
        />
      )}

      {showSaveAsImageModal && (
        <SaveAsImageModal
          onSave={handleImportAsImage}
          onClose={() => setShowSaveAsImageModal(false)}
        />
      )}

      {showBusinessFolderModal && (
        <BusinessFolderModal
          images={businessFolderImages}
          isLoading={isLoadingFolderImages}
          onSelect={handleBusinessFolderSelect}
          onClose={() => setShowBusinessFolderModal(false)}
        />
      )}
    </UserLayout>
  )
}
